import { useState, useEffect, useCallback } from 'react';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { env } from '@core/config/env';
import { captureMpSdkLoadOutcome } from '@core/sentry';
import {
  ensureMercadoPagoScript,
  removeMercadoPagoScript,
} from '@shared/utils/mercadoPagoSdkLoader';

/**
 * MercadoPago SDK v1 global type declarations.
 */
declare global {
  interface Window {
    Mercadopago: {
      setPublishableKey: (key: string) => void;
      clearSession: () => void;
      createToken: (form: HTMLFormElement, callback: (status: number, response: MercadoPagoTokenResponse) => void) => void;
      getPaymentMethod: (params: { bin: string }, callback: (status: number, response: MercadoPagoPaymentMethod[]) => void) => void;
      key?: string;
    };
  }
}

interface MercadoPagoTokenResponse {
  id?: string;
  error?: string;
  // Array of { code, description } on validation failures, but MP's
  // bin-lookup path reports a single bare object instead.
  cause?: unknown;
}

/**
 * MP v1's createToken callback reports failures as `{ status, error, cause }`
 * where `cause` carries the only actionable detail — e.g.
 * `[{ code: '324', description: 'invalid parameter docNumber' }]` or
 * "payment method not found". The SDK runs its full validation client-side,
 * so most rejections share the same opaque `error: 'bad_request'`; without
 * the cause codes in the message every failure mode collapses into one
 * undiagnosable Sentry issue (see MEITRE-FRONTEND-V2-1M).
 */
function formatMpTokenError(response: MercadoPagoTokenResponse): string {
  const base = response.error || 'Failed to create MercadoPago token';
  const causes = Array.isArray(response.cause)
    ? response.cause
    : response.cause
      ? [response.cause]
      : [];
  const detail = causes
    .map((cause) => {
      const { code, description } = (cause ?? {}) as {
        code?: string | number;
        description?: string;
      };
      return [code, description]
        .filter((part) => part !== undefined && part !== '')
        .join(': ');
    })
    .filter(Boolean)
    .join('; ');
  return detail ? `${base} (${detail})` : base;
}

interface MercadoPagoPaymentMethod {
  id: string;
  name: string;
  payment_type_id: string;
}

const POLL_INTERVAL = 250;
// Per-attempt budget. Typical fetch failures (DNS, refused, blocked) fire
// onerror within a couple of seconds and retry immediately; the timeout only
// catches stalled connections, where a fresh request is the best recovery.
const ATTEMPT_TIMEOUT_MS = 8000;
const MAX_LOAD_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 500;

interface UseMercadoPagoSDKResult {
  isReady: boolean;
  error: string | null;
  createToken: (form: HTMLFormElement) => Promise<string>;
  getPaymentMethod: (bin: string) => Promise<string>;
}

export function useMercadoPagoSDK(): UseMercadoPagoSDKResult {
  const restaurant = useRestaurant();
  const isMP = restaurant.paymentProcessor === 'mercadopago';
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const restaurantId = restaurant.id;

  useEffect(() => {
    if (!isMP) return;

    setIsReady(false);
    setError(null);

    let cancelled = false;
    let attempt = 0;
    let poll: ReturnType<typeof setInterval> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const succeed = () => {
      // Use marketplace key if restaurant has marketplace, else restaurant's own key
      const key = restaurant.hasMarketplace
        ? env.mpPublishableKey
        : restaurant.publicKeyPayment;
      window.Mercadopago.setPublishableKey(key);
      if (attempt > 1) {
        // A retry saved this session — report it so the volume/geography of
        // flaky SDK loads is measurable in Sentry.
        captureMpSdkLoadOutcome('recovered', { restaurantId, attempts: attempt });
      }
      setIsReady(true);
    };

    const startAttempt = () => {
      if (cancelled) return;
      attempt += 1;

      // The SDK assigns window.Mercadopago synchronously on eval, so a
      // defined global means the script (this attempt's or a preloaded one)
      // already executed.
      if (typeof window.Mercadopago !== 'undefined') {
        succeed();
        return;
      }

      const script = ensureMercadoPagoScript(attempt);

      // One shot per attempt: onerror and the stall timeout can both fire
      // for the same script, and failAttempt must not double-schedule.
      let settled = false;
      const failAttempt = (reason: 'script-error' | 'timeout') => {
        if (cancelled || settled) return;
        settled = true;
        if (poll) clearInterval(poll);
        // Drop the dead (or stalled) tag so the next attempt injects a
        // fresh request instead of polling a script that will never run.
        removeMercadoPagoScript();
        if (attempt < MAX_LOAD_ATTEMPTS) {
          retryTimer = setTimeout(startAttempt, RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
        } else {
          captureMpSdkLoadOutcome('failed', { restaurantId, attempts: attempt, reason });
          setError('MercadoPago SDK failed to load');
        }
      };

      script.addEventListener('error', () => failAttempt('script-error'), { once: true });

      const startTime = Date.now();
      poll = setInterval(() => {
        if (typeof window.Mercadopago !== 'undefined') {
          if (settled) return;
          settled = true;
          clearInterval(poll);
          succeed();
        } else if (Date.now() - startTime > ATTEMPT_TIMEOUT_MS) {
          failAttempt('timeout');
        }
      }, POLL_INTERVAL);
    };

    startAttempt();

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      if (retryTimer) clearTimeout(retryTimer);
      setIsReady(false);
      setError(null);
    };
  }, [isMP, restaurantId, restaurant.hasMarketplace, restaurant.publicKeyPayment]);

  const createToken = useCallback((form: HTMLFormElement): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!window.Mercadopago) {
        reject(new Error('MercadoPago SDK not loaded'));
        return;
      }
      window.Mercadopago.clearSession();
      window.Mercadopago.createToken(form, (status, response) => {
        if ((status === 200 || status === 201) && response.id) {
          resolve(response.id);
        } else {
          reject(new Error(formatMpTokenError(response)));
        }
      });
    });
  }, []);

  const getPaymentMethod = useCallback((bin: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!window.Mercadopago) {
        reject(new Error('MercadoPago SDK not loaded'));
        return;
      }
      window.Mercadopago.getPaymentMethod({ bin }, (status, response) => {
        if (status === 200 && response.length > 0) {
          resolve(response[0].id);
        } else {
          reject(new Error('Failed to detect payment method'));
        }
      });
    });
  }, []);

  return { isReady, error, createToken, getPaymentMethod };
}
